// The two stage ladders — script and footage — and the only legal moves
// between their rungs. See docs/archive/ironside-status-model-2026-07-09.md §1-§3.
//
// ScriptStage below REPLACED the same-named type in ironside-script-data.ts,
// which now re-exports it: the name survived, its five values changed. The old
// VideoStage became FootageStage.
//
// The doc calls these "statuses" throughout. The code has always called them
// stages, so stage is the word everywhere here — types, functions, metadata,
// the persisted `stage_override` row kind. Only the doc and the two Videos
// table column headers still say "status".
//
// SCRIPT and FOOTAGE ARE TWO TYPES, NOT ONE SHARED ENUM. Four of the five
// display names are identical, and three of the internal keys are too, but a
// script in `awaiting-am-review` and its footage in `awaiting-am-review` are
// different facts about the same video — the Videos table shows both at once,
// side by side. Do not unify them. That is also why there are two transition
// functions rather than one: TypeScript cannot tell the ladders apart from a
// bare "awaiting-am-review", so the ladder has to live in the function name.

import type { Persona } from "@/lib/persona-types";
import { sitsInAmSeat } from "@/lib/persona-scope";

export type ScriptStage =
  | "to-be-submitted"
  | "awaiting-am-review"
  | "edits-requested"
  | "awaiting-brand-review"
  | "approved";

export type FootageStage =
  | "to-be-uploaded"
  | "awaiting-am-review"
  | "edits-requested"
  | "awaiting-brand-review"
  | "approved";

/**
 * Uploading IS submitting; footage has no separate submit step. A re-upload is
 * the same `upload` action fired again — there is no separate verb for it,
 * because replacing the file and posting it for the first time are the same
 * event as far as the AM waiting on it is concerned.
 *
 * `undo` walks back the move THIS ROLE last made, and `remove` retracts an
 * upload. Both are BACKWARD moves, and they are the only two.
 *
 * `undo` IS NOT IN THE TRANSITION TABLES, and that is not an omission. Where
 * every other action is a function of (stage, role), an undo is a function of
 * what actually happened — the same `edits-requested` rung is reached by the AM
 * and by the Brand, and walking back to the wrong one of those hands the work to
 * a reviewer who never asked for it. So the ladder REMEMBERS its last move (a
 * StageMove, persisted beside the stage) and undo is that move read backwards.
 * See undoneScriptStage.
 */
export type ScriptAction = "submit" | "approve" | "request-edits" | "undo";
export type FootageAction = "upload" | "approve" | "request-edits" | "undo" | "remove";

/**
 * Who is acting. This is the ACTIVE persona, not the signed-in identity — an
 * admin acts by impersonating an AM or a Brand, and gets no actions of its own.
 */
export type Role = Persona["role"];

/**
 * The roles that can move a stage. `admin` acts as an AM (see `acting` below);
 * `writer` is deliberately absent.
 *
 * A WRITER'S ABSENCE FROM THE TABLES BELOW IS THE GUARD, not an omission. They
 * write and grade; they never submit. Every rule is matched on `role`, so a
 * writer firing `submit` from `to-be-submitted` finds no rule and gets null —
 * the same no-op an admin gets, through the same mechanism, with no `writer`
 * branch anywhere for someone to later "fix". See canTransitionStage.
 */
export const ACTING_ROLES = ["creator", "am", "am_ops", "creative_specialist", "admin", "brand"] as const;

/**
 * WHO HOLDS THE LAST SEAT ON THIS BRAND'S LADDERS.
 *
 * Both ladders end in an approval, and for most brands that approval is the
 * Brand's own: the AM clears the work, then the client rules on it. Some brands
 * do not want the second seat and are content for their AM's approval to be the
 * only one — so the seat is a per-brand value, and this is the ladder's word
 * for it.
 *
 * ⚠️ IT CONFIGURES THE TABLE, NOT THE READERS. Under `"am"` the rung
 * `awaiting-brand-review` is not reachable at all — the AM's approve lands on
 * `approved` — so nothing downstream ever has to ask whether a brand reviews.
 * A badge, a rollup line, a Slack notice and a dashboard count all keep asking
 * their one question of the stage in front of them, and the answer is simply
 * never a rung that should not exist. That is the whole design, chosen over
 * keeping the rung and teaching every reader to ask whether this brand reviews.
 *
 * The stored value and its default live in lib/brand-approval.ts — this is the
 * vocabulary, and it belongs to the ladder that spends it.
 */
export type FinalApprover = "brand" | "am";

/**
 * `dot` is the SAME colour as a solid fill — the editor's jump nav draws one
 * 8px disc per video, and a badge's `bg` is a 15%-opacity tint that disappears
 * at that size. The entry rung is the exception and is deliberately hollow:
 * nothing has happened to that video yet, and an outline says that in a way no
 * colour can. Keep it in step with `color`/`bg` — the dot and the badge are the
 * same claim about the same video, six inches apart on the same screen.
 */
type StageMeta = { label: string; color: string; bg: string; dot: string };

export const SCRIPT_STAGE_META: Record<ScriptStage, StageMeta> = {
  // text-muted, not text-dim: as 9px uppercase micro-caps on #252840 the dim grey
  // sat at ~1.9:1 against its own fill. The neutral rung stays the quietest of
  // the five; it does not stay unreadable.
  "to-be-submitted":       { label: "To be submitted",     color: "text-text-muted", bg: "bg-line", dot: "border border-text-dim bg-transparent" },
  "awaiting-am-review":    { label: "Awaiting AM review",  color: "text-chipfg-amber", bg: "bg-chipc-amber", dot: "bg-amber-400" },
  "edits-requested":       { label: "Edits requested",     color: "text-chipfg-red",   bg: "bg-chipc-red", dot: "bg-red-400" },
  "awaiting-brand-review": { label: "Awaiting Brand review", color: "text-chipfg-sky", bg: "bg-chipc-sky", dot: "bg-sky" },
  approved:                { label: "Approved",            color: "text-chipfg-green", bg: "bg-chipc-green", dot: "bg-green-400" },
};

// Same stage, same colour, across both ladders — the two badges sit beside each
// other on every surface, so a shared palette is what makes them comparable.
export const FOOTAGE_STAGE_META: Record<FootageStage, StageMeta> = {
  "to-be-uploaded":        { label: "To be uploaded",      color: "text-text-muted", bg: "bg-line", dot: "border border-text-dim bg-transparent" },
  "awaiting-am-review":    { label: "Awaiting AM review",  color: "text-chipfg-amber", bg: "bg-chipc-amber", dot: "bg-amber-400" },
  "edits-requested":       { label: "Edits requested",     color: "text-chipfg-red",   bg: "bg-chipc-red", dot: "bg-red-400" },
  "awaiting-brand-review": { label: "Awaiting Brand review", color: "text-chipfg-sky", bg: "bg-chipc-sky", dot: "bg-sky" },
  approved:                { label: "Approved",            color: "text-chipfg-green", bg: "bg-chipc-green", dot: "bg-green-400" },
};

// ─── The Brand's three-rung view ───────────────────────────────────────────
//
// A Brand does not run the ladder, it waits at the end of it. Every rung before
// their own turn is one fact to them — somebody else is still working on this —
// so the five stages collapse to three: In progress, Awaiting review (yours),
// Approved. The internal ladder is unchanged; this is a projection of it, and
// only the Brand's surfaces use it.
//
// "Edits requested" collapses INTO In progress on purpose, including edits the
// Brand asked for themselves: the answer to "what do I do about it" is the same
// as every other in-progress rung — nothing yet, it is coming back.
export type BrandStage = "in-progress" | "awaiting-review" | "approved";

export const BRAND_STAGE_META: Record<BrandStage, StageMeta> = {
  "in-progress":     { label: "In progress",     color: "text-text-muted", bg: "bg-line", dot: "border border-text-dim bg-transparent" },
  // Amber, where the full ladder paints this rung sky blue. On the Brand's page
  // it is the one row that is waiting on THEM, so it is the only status worth
  // spending a colour on.
  "awaiting-review": { label: "Awaiting review",  color: "text-amber-400",  bg: "bg-chipc-amber", dot: "bg-amber-400" },
  approved:          { label: "Approved",         color: "text-green-400",  bg: "bg-chipc-green", dot: "bg-green-400" },
};

/** Both ladders' five rungs, projected onto the Brand's three. */
export function brandStage(stage: ScriptStage | FootageStage): BrandStage {
  if (stage === "approved") return "approved";
  if (stage === "awaiting-brand-review") return "awaiting-review";
  return "in-progress";
}

/**
 * How much of a video the Brand's editor shows.
 *
 *   "line"      one line: Video N and a status. No title, no document, nothing
 *               to open. The script is being written or rewritten, and it is not
 *               theirs to read yet — a collapsed row here would be an invitation
 *               to open something that is not ready.
 *   "open"      the document, open and not closable. Something is waiting on
 *               their review, which is the only reason they were sent the link.
 *   "collapsed" the title, click to read. Work they have already been party to:
 *               still there, not in the way.
 *
 * A FUNCTION OF THE STAGES — AND OF ONE FACT THE STAGES CANNOT HOLD. Which shape
 * a row takes is mostly the question "is this mine to do anything about", and the
 * ladders answer that. `footage` is null until the script is approved
 * (footageBadgeFor), so "script approved" and "footage exists" are the same fact
 * and only one of them needs asking about.
 *
 * ─── WHY THERE IS A THIRD ARGUMENT ──────────────────────────────────────────
 *
 * `edits-requested` IS REACHED BY TWO DIFFERENT PEOPLE, and the stage does not
 * record which. It is where the AM sends a script back — which the Brand has
 * never seen, and which "not theirs to read yet" describes exactly — and it is
 * where the BRAND sends one back, which they have just read, just ruled on, and
 * may well want to return to in order to say what they actually want changed.
 *
 * Those two want opposite shapes, and for a while both got "line": a Brand
 * pressed Request edits and the script vanished into a grey one-line row with no
 * title, no document and no comment rail — no way back to the thread where the
 * edits they were asking for would have been written down. That is the second
 * half of the bug reported as "I clicked Request edits and nothing happened".
 *
 * So the caller passes the half the ladder cannot know. This is the same shape of
 * answer undoneScriptStage needs and for the same reason — see the note above
 * SCRIPT_RULES: where two people can reach one rung, the rung is not the fact,
 * the RECORD is.
 */
export type BrandRowShape = "line" | "open" | "collapsed";

export function brandRowShape(
  script: ScriptStage,
  footage: FootageStage | null,
  /**
   * Is the move standing on this script ladder the Brand's OWN?
   *
   * Read from the undo record (`undoPointFor("script", id)?.role === "brand"`),
   * which means the window is the same one their Undo button lives in: they can
   * reopen what they sent back for exactly as long as nobody has acted on it.
   * The moment the creator re-submits, the row is somebody else's work in flight
   * again and goes back to being a line — and when it next comes round to them
   * it is "open" anyway.
   *
   * PAIRING IT WITH THE UNDO IS THE POINT, not a coincidence of implementation:
   * the row that still carries "Undo request edits" is the row that can still be
   * opened, so the two controls on it never disagree about whether this is still
   * the Brand's to touch.
   */
  brandActed = false,
): BrandRowShape {
  // Footage first: once the script is approved, the script ladder is history and
  // the video is the question in front of them.
  if (footage !== null) return brandStage(footage) === "awaiting-review" ? "open" : "collapsed";
  if (brandStage(script) === "awaiting-review") return "open";
  return brandActed ? "collapsed" : "line";
}

// ─── The transition tables — §2 ────────────────────────────────────────────
//
// `edits-requested` always returns to the Creator and always re-enters the
// ladder at Awaiting AM review. A BRAND-REQUESTED EDIT IS RE-REVIEWED BY THE AM
// BEFORE IT REACHES THE BRAND AGAIN. There is no shortcut back to the Brand.
//
// `approved` USED TO BE TERMINAL, and the note here read "no undo … (Not
// requested. Noted because it will be asked for.)" It was asked for — first as
// one admin-only Un-approve per ladder, and now as the general rule those two
// buttons were a special case of: EVERY ROLE MAY WALK BACK ITS OWN LAST MOVE,
// for as long as nobody downstream has acted on it.
//
// ─── Undo is not a row in these tables ─────────────────────────────────────
//
// Every rule below answers "where does this stage go", from the stage and the
// role alone. An undo cannot be written that way, because two of the rungs are
// reached by two different people: `edits-requested` is where the AM sends work
// back AND where the Brand does, and an undo that guessed would hand a Brand's
// rejection back to an AM who never made it. `awaiting-am-review` is the same
// story from the other end — a creator's submit and a creator's re-submit land
// there from different rungs, and only one of them came from `edits-requested`.
//
// So the answer is not derived, it is REMEMBERED. `undoable` marks the moves
// worth remembering; the store writes the move that was actually made (a
// StageMove, persisted beside the stage itself) and undoneScriptStage reads it
// backwards. The tables stay a statement about the ladder, and the history stays
// a statement about this video.
//
// `adminOnly` IS GONE, and with it the second actor fact the two functions used
// to take. It existed for exactly one reason — un-approve was the Brand's rung
// but not the Brand's privilege — and that reason retired with the button: a
// Brand retracting the approval they just gave is now the ordinary case, not the
// admin correction. An admin still acts through a lens and still has no rules of
// its own (§7), which is the part that never depended on the flag.

type Rule<S, A> = {
  from: S;
  action: A;
  role: Role;
  to: S;
  /**
   * May the actor take this move back? Marks the moves the store records an
   * undo point for, and nothing else — an unmarked move leaves no undo point,
   * so it also CLEARS any point standing from before it.
   *
   * The creator's `upload` and `remove` are the two deliberate omissions on
   * either ladder. Not because they are irreversible — they are the most
   * reversible moves in the app — but because they already reverse each other
   * in plain words. "Undo upload" beside "Remove video" would be two buttons
   * for one act, and the second one says what it does.
   */
  undoable?: true;
};

// ─── ONE GENERATOR, TWO TABLES, AND THE SEAT IS THE ONLY DIFFERENCE ────────
//
// A brand whose last seat is their AM's does not get a SHORTER ladder with a
// condition on the end of it — it gets a ladder on which `awaiting-brand-review`
// IS NOT REACHABLE AT ALL, because the AM's approve lands on `approved`. That is
// what keeps the rest of the app out of this: a reader is never handed a rung it
// would have to discount, so it never has to ask whether this brand reviews.
//
// Both tables come out of one function so they cannot drift. Exactly two rules
// per ladder differ, and both differ in the same word.
const scriptRules = (by: FinalApprover): Rule<ScriptStage, ScriptAction>[] => [
  // EVERY FORWARD MOVE ON THIS LADDER IS UNDOABLE. Each one hands the work to
  // somebody else, and the window closes the moment that somebody acts — see
  // undoneScriptStage, where "nobody has moved it since" is the whole test.
  { from: "to-be-submitted",       action: "submit",        role: "creator", to: "awaiting-am-review",    undoable: true },
  { from: "edits-requested",       action: "submit",        role: "creator", to: "awaiting-am-review",    undoable: true },
  { from: "awaiting-am-review",    action: "request-edits", role: "am",      to: "edits-requested",       undoable: true },

  // THE SEAT. The AM's approve either hands the script on, or ends the ladder.
  { from: "awaiting-am-review",    action: "approve",       role: "am",
    to: by === "brand" ? "awaiting-brand-review" : "approved",                                            undoable: true },

  // THE LAST RUNG BELONGS TO WHOEVER HOLDS THE SEAT — `role: by`, which is the
  // sentence this whole design reduces to.
  //
  // Under `"am"` the rung is unreachable, so these two look like dead rules. They
  // are THE DRAIN, and they are what makes the switch total rather than a
  // migration: a brand that stops reviewing may have work standing on
  // `awaiting-brand-review` at that moment, and without them that work is on a
  // rung with no legal move off it, for anybody, forever. Written this way the
  // backlog is simply cleared by the hands the seat changed to — and the reverse
  // flip is the mirror, with no stored stage touched in either direction.
  { from: "awaiting-brand-review", action: "approve",       role: by,        to: "approved",              undoable: true },
  { from: "awaiting-brand-review", action: "request-edits", role: by,        to: "edits-requested",       undoable: true },
];

/** The script ladder, per seat. Built once — these are tables, not decisions. */
const SCRIPT_RULES: Record<FinalApprover, Rule<ScriptStage, ScriptAction>[]> = {
  brand: scriptRules("brand"),
  am: scriptRules("am"),
};

// The footage twin — the same two rules differ, in the same word. Its four
// creator rules below are seat-blind: uploading and withdrawing a video are the
// creator's own business either way.
const footageRules = (by: FinalApprover): Rule<FootageStage, FootageAction>[] => [
  { from: "to-be-uploaded",        action: "upload",        role: "creator", to: "awaiting-am-review" },
  { from: "awaiting-am-review",    action: "request-edits", role: "am",      to: "edits-requested",       undoable: true },
  { from: "edits-requested",       action: "upload",        role: "creator", to: "awaiting-am-review" },

  // THE SEAT, and THE DRAIN — see scriptRules, where both are argued at length.
  { from: "awaiting-am-review",    action: "approve",       role: "am",
    to: by === "brand" ? "awaiting-brand-review" : "approved",                                            undoable: true },
  { from: "awaiting-brand-review", action: "approve",       role: by,        to: "approved",              undoable: true },
  { from: "awaiting-brand-review", action: "request-edits", role: by,        to: "edits-requested",       undoable: true },

  // ─── The creator's two second thoughts ───────────────────────────────────
  //
  // BOTH STOP AT THE AM. A creator may replace or withdraw their video for
  // exactly as long as nobody senior has acted on it; the moment the AM
  // approves (`awaiting-brand-review`) the file is in front of the client and
  // is no longer theirs to swap out from underneath. That is the same line the
  // ladder already drew for re-upload, now drawn for `remove` too.
  //
  // A SELF-LOOP, and it has to be. Re-uploading while the AM is still looking
  // leaves the stage exactly where it was — the AM's queue is unchanged,
  // because the video was already in it. Returning null here instead would make
  // recordDriveVideo drop the paste and the creator would be stuck with the
  // first file.
  { from: "awaiting-am-review",    action: "upload",        role: "creator", to: "awaiting-am-review" },
  // Removing is retracting the submission: the slot goes back to empty and the
  // week reads "To be uploaded" again, which is exactly what is now true of it.
  { from: "awaiting-am-review",    action: "remove",        role: "creator", to: "to-be-uploaded" },
  { from: "edits-requested",       action: "remove",        role: "creator", to: "to-be-uploaded" },
];

/** The footage ladder, per seat. */
const FOOTAGE_RULES: Record<FinalApprover, Rule<FootageStage, FootageAction>[]> = {
  brand: footageRules("brand"),
  am: footageRules("am"),
};

// AM OPS, A CREATIVE SPECIALIST AND ADMIN HOLD AN AM'S KEYS. Every rule above is
// written for `am`, and all three match those rules rather than a second copy of them — sitsInAmSeat is
// where that fact is written down, for this file and the four others that used
// to repeat it. The audit still records the real lens: actor_role comes from the
// persona, not from this lookup.
const acting = (role: Role): Role => (sitsInAmSeat(role) ? "am" : role);

function ruleFor<S, A>(rules: Rule<S, A>[], from: S, action: A, role: Role): Rule<S, A> | undefined {
  const r = acting(role);
  return rules.find((x) => x.from === from && x.action === action && x.role === r);
}

/**
 * The next script stage, or `null` if that actor cannot fire that action from
 * that stage. `null` is the answer for every combination absent from §2 —
 * including every action an `admin` PERSONA attempts, and including `undo`,
 * which is not in the table at all (see undoneScriptStage).
 *
 * Callers must treat `null` as "no-op", never as "leave it alone and carry on":
 * this is the guard, not the UI's button visibility.
 *
 * `role` IS THE LENS, and it is now the only thing asked about the actor. The
 * `isAdmin` argument this used to take went with un-approve.
 *
 * `by` IS THE BRAND'S, NOT THE ACTOR'S — which table this move is being read
 * out of. The store resolves it from the script's own brand
 * (finalApproverFor), so no caller in the app has to carry it.
 */
export function nextScriptStage(from: ScriptStage, action: ScriptAction, role: Role, by: FinalApprover): ScriptStage | null {
  return ruleFor(SCRIPT_RULES[by], from, action, role)?.to ?? null;
}

/** The footage twin of nextScriptStage. Same contract. */
export function nextFootageStage(from: FootageStage, action: FootageAction, role: Role, by: FinalApprover): FootageStage | null {
  return ruleFor(FOOTAGE_RULES[by], from, action, role)?.to ?? null;
}

// ─── Undo — the move a ladder remembers ────────────────────────────────────

/**
 * The move that put a ladder on the rung it is standing on. One per ladder per
 * video, overwritten by the next move and erased by the undo that spends it.
 *
 * `to` IS STORED EVEN THOUGH IT IS USUALLY THE CURRENT STAGE. It is what makes
 * the record self-checking: a second tab that moved the ladder on while this one
 * was showing an Undo button leaves a record whose `to` no longer matches, and
 * the button dies on the next render rather than firing a move computed from a
 * history that has been overtaken.
 */
export interface StageMove<S, A> {
  from: S;
  to: S;
  action: A;
  /** The LENS that acted — an admin reviewing as the Brand records `brand`. */
  role: Role;
}

export type ScriptMove = StageMove<ScriptStage, ScriptAction>;
export type FootageMove = StageMove<FootageStage, FootageAction>;

/** Is this move one whose actor gets to take it back? See Rule.undoable. */
export function scriptMoveIsUndoable(from: ScriptStage, action: ScriptAction, role: Role, by: FinalApprover): boolean {
  return ruleFor(SCRIPT_RULES[by], from, action, role)?.undoable === true;
}

/** The footage twin. `upload` and `remove` are the two that answer false. */
export function footageMoveIsUndoable(from: FootageStage, action: FootageAction, role: Role, by: FinalApprover): boolean {
  return ruleFor(FOOTAGE_RULES[by], from, action, role)?.undoable === true;
}

/**
 * The rung an undo would return to, or `null` if this actor has nothing to undo.
 *
 * THREE QUESTIONS, AND ALL THREE ARE THE POINT:
 *
 *   is there a move to undo    — an unmarked move leaves no record, and a spent
 *                                undo erases the one it used. No record, no undo.
 *   did THIS ROLE make it      — `role` is the whole per-role rule. An AM looking
 *                                at `edits-requested` the Brand asked for finds a
 *                                record that is not theirs and gets no button.
 *   has nothing moved since    — `to` against the stage as it stands now. This is
 *                                the window: the creator may unsubmit until the
 *                                AM acts, the AM may unapprove until the Brand
 *                                does, because the act of the person downstream
 *                                is what changes the stage out from under it.
 *
 * The window is therefore not a separate rule anywhere. It falls out of the
 * ladder: acting on somebody's work IS the thing that takes their undo away.
 *
 * ⚠️ AND IT TAKES NO SEAT (FinalApprover). An undo is read from the RECORD, never
 * from a table, so there is no table to choose. That also settles the awkward
 * case for free: a Brand's approve, recorded before their brand handed the seat
 * to its AM, fails the "did THIS ROLE make it" test the moment the AM is the one
 * looking — the old occupant's undo goes with the seat, which is right, and
 * nothing had to say so.
 */
function undone<S, A>(move: StageMove<S, A> | undefined, current: S, role: Role): S | null {
  if (!move) return null;
  if (move.role !== role) return null;
  if (move.to !== current) return null;
  return move.from;
}

/**
 * Script undo, with the one rule that is not about the script: a script may not
 * leave `approved` while its video is standing on it (canUnapproveScript).
 *
 * That guard is why this takes a footage stage it otherwise has no use for. It
 * is the same cross-ladder rule un-approve had, and it did not retire with the
 * button — the Brand undoing the approval that unlocked filming is exactly the
 * case it was written for.
 */
export function undoneScriptStage(
  move: ScriptMove | undefined,
  current: ScriptStage,
  role: Role,
  footage: FootageStage,
): ScriptStage | null {
  const to = undone(move, current, role);
  if (to === null) return null;
  if (current === "approved" && !canUnapproveScript(footage)) return null;
  return to;
}

/** Footage undo. No cross-ladder rule — nothing stands on the footage ladder. */
export function undoneFootageStage(
  move: FootageMove | undefined,
  current: FootageStage,
  role: Role,
): FootageStage | null {
  return undone(move, current, role);
}

/** Footage does not exist until its script is approved (§1). */
export function footageUnlocked(scriptStage: ScriptStage): boolean {
  return scriptStage === "approved";
}

/**
 * May a script's approval be retracted, given where its footage has got to?
 *
 * THE ROLLBACK IS ORDERED, and this is what orders it. Un-approving a script is
 * the one move that can invalidate the OTHER ladder: footage exists only while
 * its script is approved (§1), so pulling the script back out of `approved`
 * under a video that is up would leave that video's stage row stranded — still
 * saying `awaiting-brand-review` or even `approved`, still in the database,
 * rendering as "—" everywhere because footageBadgeFor now refuses to show it.
 * An approved video that nobody can see is worse than a refused click.
 *
 * So the video comes down first: the creator removes it (footage returns to
 * `to-be-uploaded`), and only then may the script approval be undone. Which is
 * the order a rollback happens in anyway — you undo the last thing first.
 *
 * Note this is a question about the FOOTAGE alone. Whether the script is in a
 * stage that can be un-approved at all is the transition table's business, and
 * asking it here as well would put the same rule in two places.
 */
export function canUnapproveScript(footage: FootageStage): boolean {
  return footage === "to-be-uploaded";
}

/**
 * Which footage stage to show beside a script, or `null` to render "—".
 *
 * The one place that decision lives. The Videos table and the editor both call
 * it, so a video cannot read "To be uploaded" on one surface and "—" on the
 * other. Per video, not per week: video 3 can be awaiting footage while script 4
 * is still in review.
 *
 * Returns null for an unapproved script EVEN IF a footage stage exists for it.
 * A `video_stage` row can outlive the approval that justified it — nothing
 * deletes rows — and a badge is not a reason to believe the footage is real.
 */
export function footageBadgeFor(script: ScriptStage, footage: FootageStage): FootageStage | null {
  return footageUnlocked(script) ? footage : null;
}
